// Mapa de colonias (Leaflet + basemap CARTO según el tema), mismo patrón que
// Atención Ciudadana. Solo puntos agregados: nunca datos de una persona.

import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export interface MapPoint {
  lat: number | null;
  lon: number | null;
  label: string;
  color: string;
  radius?: number;
  detail?: string;
}

// Basemap: CARTO con llave (VITE_CARTO_API_KEY; parámetro documentado `key`, igual que
// Atención Ciudadana). Sin llave NO se piden teselas (CARTO marca «API KEY REQUIRED» y
// la política de OSM desalienta uso en producción): se pinta solo la capa de datos
// sobre el fondo de la tarjeta, con un aviso discreto.
const CARTO_KEY = ((import.meta.env.VITE_CARTO_API_KEY as string | undefined) || '').trim();
const isDark = () => document.documentElement.getAttribute('data-theme') === 'dark';

function tileUrl(): string {
  return `https://{s}.basemaps.cartocdn.com/${isDark() ? 'dark_all' : 'light_all'}/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(CARTO_KEY)}`;
}

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (t: string) => String(t ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

export function GovMap({ points, height = 320, zoom = 12, label }: { points: MapPoint[]; height?: number; zoom?: number; label: string }) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const tileRef = useRef<L.TileLayer | null>(null);
  const [ready, setReady] = useState(0);

  // Crear y destruir en el MISMO efecto (seguro con StrictMode y al cambiar de pestaña).
  useEffect(() => {
    if (!nodeRef.current) return;
    const map = L.map(nodeRef.current, { zoomControl: true, attributionControl: true, scrollWheelZoom: false })
      .setView([22.1498, -100.9792], zoom);
    tileRef.current = CARTO_KEY
      ? L.tileLayer(tileUrl(), { attribution: '© OpenStreetMap · © CARTO', maxZoom: 19, subdomains: 'abcd' }).addTo(map)
      : null;
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    const t = setTimeout(() => map.invalidateSize(), 60);
    const obs = new MutationObserver(() => tileRef.current?.setUrl(tileUrl()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    setReady((n) => n + 1);
    return () => { clearTimeout(t); obs.disconnect(); map.remove(); mapRef.current = null; layerRef.current = null; };
  }, [zoom]);

  useEffect(() => {
    const g = layerRef.current;
    if (!g) return;
    g.clearLayers();
    points.forEach((p) => {
      if (p.lat == null || p.lon == null) return;
      L.circleMarker([p.lat, p.lon], {
        radius: p.radius ?? 9, color: p.color, weight: 2, fillColor: p.color, fillOpacity: 0.35,
      }).bindTooltip(`<strong>${esc(p.label)}</strong>${p.detail ? `<br/>${esc(p.detail)}` : ''}`).addTo(g);
    });
  }, [points, ready]);

  return (
    <div className="relative">
      <div
        ref={nodeRef}
        role="img"
        aria-label={label}
        className="gov-map relative z-0 w-full overflow-hidden rounded-2xl border border-border bg-muted/40"
        style={{ height }}
      />
      {!CARTO_KEY && (
        <p className="pointer-events-none absolute bottom-2 left-2 z-[1] rounded-md bg-card/90 px-2 py-0.5 text-[10px] text-muted-foreground">
          Mapa base no configurado · se muestran solo los datos
        </p>
      )}
    </div>
  );
}
